import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useSiteSettings } from "../hooks/useSiteContent";
import Logo from "./Logo";

export const Footer = () => {
  const settings = useSiteSettings() || {};
  const email = settings.email || "info@cmlindleyllc.com";
  const phone = settings.phone || "903-521-2788";
  const addr1 = settings.address_line1 || "476 Highway A1A, Suite 8B";
  const addr2 = settings.address_line2 || "Satellite Beach, FL 32937";
  const mailing = settings.mailing_address || "PO Box 372913 Satellite Beach, FL 32937";
  const blurb = settings.footer_blurb ||
    "Premier material supply for builders and contractors. From dimensional lumber to structural steel, CM Lindley delivers quality on schedule, every time.";
  const tagline = settings.tagline || "Material Supply Specialists";

  return (
    <footer data-testid="site-footer" className="bg-[#0A192F] text-white">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-12 sm:pt-14 pb-10 sm:pb-12 grid md:grid-cols-12 gap-8 sm:gap-12">
        <div className="md:col-span-5">
          <div data-testid="footer-logo" className="mb-6 sm:mb-7">
            <Logo variant="white" size="footer" />
          </div>
          <p className="text-white/70 max-w-md leading-relaxed text-sm">{blurb}</p>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-display text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 sm:mb-5">
            Navigate
          </h4>
          <ul className="space-y-3 text-sm">
            {[
              ["Home", "/"],
              ["Services", "/services"],
              ["About", "/about"],
              ["Contact", "/contact"],
            ].map(([label, to]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={`footer-link-${label.toLowerCase()}`}
                  className="text-white/80 hover:text-[#D4AF37] transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-4">
          <h4 className="font-display text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4 sm:mb-5">
            Contact
          </h4>
          <ul className="space-y-4 text-sm text-white/80">
            <li className="flex items-start gap-3">
              <Mail size={16} className="mt-0.5 text-[#D4AF37] flex-shrink-0" />
              <a
                href={`mailto:${email}`}
                data-testid="footer-email"
                className="hover:text-[#D4AF37] transition-colors break-all"
              >
                {email}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={16} className="mt-0.5 text-[#D4AF37] flex-shrink-0" />
              <a
                href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                data-testid="footer-phone"
                className="hover:text-[#D4AF37] transition-colors"
              >
                {phone}
              </a>
            </li>
            <li className="flex items-start gap-3">
              <MapPin size={16} className="mt-0.5 text-[#D4AF37] flex-shrink-0" />
              <div data-testid="footer-address" className="leading-snug">
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 font-display font-bold mb-1">
                  Office
                </div>
                <div>
                  {addr1}
                  <br />
                  {addr2}
                </div>
                <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80 font-display font-bold mb-1">
                  Mailing
                </div>
                <div data-testid="footer-mailing">{mailing}</div>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 py-5 sm:py-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-white/50">
          <span>© {new Date().getFullYear()} CM Lindley LLC. All rights reserved.</span>
          <span className="font-display tracking-[0.25em] sm:tracking-[0.3em] uppercase">{tagline}</span>
          <span data-testid="footer-design-credit">
            Website Designed By{" "}
            <a
              href="https://bp.agency"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-brand-partners-link"
              className="font-bold text-white hover:text-[#D4AF37] transition-colors"
            >
              Brand Partners
            </a>
          </span>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
